import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { streamChat, ChatMessage, PriceRow } from '../services/inferenceApi';

export interface TurnUsage {
  prompt: number;
  completion: number;
  cost: number;
}

/** Wall-clock timings for one assistant turn, in milliseconds. */
export interface TurnTiming {
  /** Request sent → first streamed token (content or reasoning). */
  ttft: number | null;
  /** Request sent → stream finished (or stopped). */
  total: number;
}

export interface ChatSessionOptions {
  model: string;
  temperature: number;
  maxTokens: number;
  apiKeyOverride: string | null;
  priceByName: Map<string, PriceRow>;
  /** Shown in toasts so parallel sessions can be told apart. */
  label?: string;
  /** Called with the request body each time a turn starts (Inspector). */
  onRequest?: (body: Record<string, unknown>) => void;
  /** Called with every raw streamed delta (Inspector). */
  onRawDelta?: (delta: string) => void;
}

const omit = <T,>(rec: Record<number, T>, key: number): Record<number, T> => {
  const { [key]: _drop, ...rest } = rec;
  return rest;
};

/**
 * One streaming chat conversation: messages plus per-turn reasoning, usage,
 * finish reason and timing, keyed by the assistant message's index. The
 * Playground uses one; compare mode runs two side by side.
 */
export function useChatSession(opts: ChatSessionOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [usageByIndex, setUsageByIndex] = useState<Record<number, TurnUsage>>({});
  // Thinking tokens from reasoning models, kept out of ChatMessage so the
  // request payload sent back to the API stays plain role/content.
  const [reasoningByIndex, setReasoningByIndex] = useState<Record<number, string>>({});
  // finish_reason per assistant message; "length" = cut off by max_tokens.
  const [finishByIndex, setFinishByIndex] = useState<Record<number, string>>({});
  const [timingByIndex, setTimingByIndex] = useState<Record<number, TurnTiming>>({});
  const [errorByIndex, setErrorByIndex] = useState<Record<number, string>>({});
  const abortRef = useRef<AbortController | null>(null);

  const run = async (next: ChatMessage[], tokens: number) => {
    const { model, temperature, apiKeyOverride, priceByName, label, onRequest, onRawDelta } = opts;
    const assistantIndex = next.length;
    // Reset per-index state in case this index is being re-streamed (retry).
    setReasoningByIndex((cur) => omit(cur, assistantIndex));
    setUsageByIndex((cur) => omit(cur, assistantIndex));
    setFinishByIndex((cur) => omit(cur, assistantIndex));
    setTimingByIndex((cur) => omit(cur, assistantIndex));
    setErrorByIndex((cur) => omit(cur, assistantIndex));
    setMessages([...next, { role: 'assistant', content: '' }]);
    setBusy(true);
    onRequest?.({ model, messages: next, stream: true, temperature, max_tokens: tokens });

    const started = performance.now();
    let firstToken: number | null = null;
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const { finishReason } = await streamChat(
        model,
        next,
        { temperature, max_tokens: tokens },
        (delta, kind) => {
          if (firstToken === null) firstToken = performance.now();
          onRawDelta?.(delta);
          if (kind === 'reasoning') {
            setReasoningByIndex((cur) => ({ ...cur, [assistantIndex]: (cur[assistantIndex] || '') + delta }));
            return;
          }
          setMessages((cur) => {
            const updated = [...cur];
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              content: updated[updated.length - 1].content + delta,
            };
            return updated;
          });
        },
        controller.signal,
        apiKeyOverride,
        (usage) => {
          const price = priceByName.get(model);
          const cost = price
            ? (usage.prompt_tokens / 1e6) * price.input_price_per_1m_inr +
              (usage.completion_tokens / 1e6) * price.output_price_per_1m_inr
            : 0;
          setUsageByIndex((cur) => ({
            ...cur,
            [assistantIndex]: { prompt: usage.prompt_tokens, completion: usage.completion_tokens, cost },
          }));
        }
      );
      if (finishReason) setFinishByIndex((cur) => ({ ...cur, [assistantIndex]: finishReason }));
    } catch (e: any) {
      if (e.name !== 'AbortError') {
        const msg = e.message || 'Request failed';
        setErrorByIndex((cur) => ({ ...cur, [assistantIndex]: msg }));
        toast.error(label ? `${label}: ${msg}` : msg);
      }
    } finally {
      const ttft = firstToken === null ? null : firstToken - started;
      setTimingByIndex((cur) => ({ ...cur, [assistantIndex]: { ttft, total: performance.now() - started } }));
      if (abortRef.current === controller) abortRef.current = null;
      setBusy(false);
    }
  };

  /** Appends a user turn and streams the reply. Returns false if it couldn't send. */
  const send = (text: string): boolean => {
    const content = text.trim();
    if (!content || !opts.model || busy) return false;
    run([...messages, { role: 'user', content }], opts.maxTokens);
    return true;
  };

  // A truncated ("length") last answer can be retried with a bigger budget:
  // drop it and re-stream the same conversation.
  const retryTruncated = (tokens: number) => {
    if (busy || messages[messages.length - 1]?.role !== 'assistant') return;
    run(messages.slice(0, -1), tokens);
  };

  const stop = () => abortRef.current?.abort();

  const clear = () => {
    setMessages([]);
    setUsageByIndex({});
    setReasoningByIndex({});
    setFinishByIndex({});
    setTimingByIndex({});
    setErrorByIndex({});
  };

  return {
    messages,
    busy,
    usageByIndex,
    reasoningByIndex,
    finishByIndex,
    timingByIndex,
    errorByIndex,
    send,
    retryTruncated,
    stop,
    clear,
  };
}

export type ChatSession = ReturnType<typeof useChatSession>;
